import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatIsoDateToPtBr } from '@/lib/utils';
import type { Amendment, Institution } from '@/services/firestore';
import { Globe, ExternalLink, MapPin, Phone, UserCheck, Link as LinkIcon, Facebook, Instagram, Youtube, MessageCircle, History } from 'lucide-react';
import { AmendmentTimeline, type HistoryItem } from './AmendmentTimeline';
import { getAmendmentHistory } from '@/services/firestore';
import { useState, useEffect } from 'react';
const TikTokIcon = (props: { className?: string }) => (
  <svg className={props.className || ''} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 2.71 3.5 2.38 1.18-.23 2.21-1.17 2.49-2.39.06-1.37.02-2.73.01-4.13.01-4.91-.01-9.83.01-14.75Z" /></svg>
);
const XIcon = (props: { className?: string }) => (
  <svg className={props.className || ''} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

import { FileText, Calendar, Coins, Paperclip } from 'lucide-react';

type LinkItem = { label: string; url?: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amendment: Amendment;
  institution: Institution | null;
  links: LinkItem[];
};

export function InstitutionAmendmentModal(props: Props) {
  const { open, onOpenChange, amendment, institution, links } = props;
  const inst = institution;
  const statusLabel = amendment.status || '—';
  const officialCode = amendment.officialCode || '—';
  const formattedDate = formatIsoDateToPtBr(amendment.date);
  const PLACEHOLDER = "data:image/svg+xml;charset=utf-8," + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect fill='#e5e7eb' width='80' height='80'/><text x='40' y='44' text-anchor='middle' font-size='12' fill='#6b7280' font-family='Inter, Arial'>Logo</text></svg>");
  const logoSrc = (inst?.logoUrl && inst.logoUrl.trim().length > 0) ? inst.logoUrl : PLACEHOLDER;

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (open && amendment?.id) {
      setLoadingHistory(true);
      getAmendmentHistory(amendment.id)
        .then(setHistory)
        .catch(() => setHistory([]))
        .finally(() => setLoadingHistory(false));
    }
  }, [open, amendment?.id]);

  const hasDetail = !!(amendment.objectDetail && amendment.objectDetail.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[100] p-0 bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto focus:outline-none">
        <DialogHeader className="sr-only">
          <DialogTitle>Detalhes da Emenda</DialogTitle>
          <DialogDescription>Visualize informações completas da emenda e da instituição</DialogDescription>
        </DialogHeader>


        <div className="flex flex-col items-center pt-8 pb-6 px-6 border-b border-gray-100 bg-gray-50/50">
          <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4 shadow-sm border border-blue-200 overflow-hidden">
            <img
              src={logoSrc}
              alt="Logo"
              className="h-16 w-16 sm:h-20 sm:w-20 rounded object-contain"
              width={80}
              height={80}
              loading="lazy"
              decoding="async"
              onError={(e) => { const t = e.target as HTMLImageElement; t.src = PLACEHOLDER; }}
            />
          </div>
          <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Instituição Beneficiada</span>
          <h2 className="text-xl font-bold text-gray-900 text-center leading-tight mb-1">{inst?.name || '—'}</h2>
          <p className="text-sm text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded mt-1">CNPJ: {inst?.cnpj || '—'}</p>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Coluna Principal: Dados da Emenda */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/5 rounded-bl-full -mr-4 -mt-4"></div>
              <div className="flex flex-wrap justify-between items-start mb-6 gap-2">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-slate-500"><path d="M4 3h16v2H4V3zm0 4h16v2H4V7zm0 4h16v2H4v-2zm0 4h16v2H4v-2z" /></svg>
                  Dados da Emenda
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                    {statusLabel}
                  </span>
                  {amendment.detailingSaved ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      Salvo e Bloqueado
                    </span>
                  ) : hasDetail ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                      Salvo como Rascunho
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                      Aguardando preenchimento
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase">Número Completo</span>
                    <span className="font-mono text-slate-700 font-medium text-lg">{officialCode}</span>
                  </div>
                  <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase">Data</span>
                    <span className="text-slate-700 text-lg">{formattedDate}</span>
                  </div>
                </div>

                <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                  <span className="text-slate-500 text-xs uppercase">Situação Federal</span>
                  <span className="text-slate-700 font-medium text-base">{amendment.federalStatus || '—'}</span>
                </div>

                <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                  <span className="text-slate-500 text-xs uppercase">Categoria da Emenda</span>
                  <span className="text-slate-700 font-medium text-base">{amendment.category || '—'}</span>
                </div>

                <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                  <span className="text-slate-500 text-xs uppercase">Nome do Projeto / Ação</span>
                  <span className="text-slate-700 font-medium text-base">{amendment.workPlan?.projectName || '—'}</span>
                </div>

                {/* Informações detalhadas se houver */}
                <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                  <span className="text-slate-500 text-xs uppercase flex items-center gap-1"><Coins className="w-3 h-3" /> Classificação da Emenda</span>
                  <span className="text-slate-700 font-medium">{amendment.classification || '—'}</span>
                </div>

                {amendment.workPlan?.purpose && (
                  <div className="flex flex-col gap-1 text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase flex items-center gap-1"><FileText className="w-3 h-3" /> Finalidade de uso</span>
                    <span className="text-slate-700 whitespace-pre-wrap leading-relaxed">{amendment.workPlan.purpose}</span>
                  </div>
                )}
                {amendment.objectDetail && (
                  <div className="flex flex-col gap-1 text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase flex items-center gap-1"><FileText className="w-3 h-3" /> Detalhamento do Objeto</span>
                    <span className="text-slate-700 whitespace-pre-wrap leading-relaxed">{amendment.objectDetail}</span>
                  </div>
                )}
                {amendment.workPlan?.period && (
                  <div className="flex flex-col text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase flex items-center gap-1"><Calendar className="w-3 h-3" /> Período de Execução</span>
                    <span className="text-slate-700">{amendment.workPlan.period}</span>
                  </div>
                )}
                {amendment.attachments && amendment.attachments.length > 0 && (
                  <div className="flex flex-col gap-2 text-sm border-b border-slate-200/60 pb-2">
                    <span className="text-slate-500 text-xs uppercase flex items-center gap-1"><Paperclip className="w-3 h-3" /> Anexos Enviados</span>
                    <div className="flex flex-wrap gap-2">
                      {amendment.attachments.map((att, idx) => (
                        <a
                          key={idx}
                          href={att}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:border-blue-300 hover:text-blue-600 rounded-md text-sm transition-all shadow-sm"
                        >
                          <ExternalLink className="w-3 h-3" /> Anexo {idx + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {amendment.signedLink ? (
                  <div className="pt-2">
                    <a href={amendment.signedLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full gap-2 p-3 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                      <ExternalLink className="w-4 h-4" />
                      Consulta Oficial no Portal
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Coluna Lateral: Contatos e Linha do Tempo */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Contatos</h4>
              <div className="space-y-3">
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1"><MapPin className="w-3 h-3" /> Endereço</span>
                  <span className="text-sm text-gray-700 mt-0.5 ml-4">—</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1"><Phone className="w-3 h-3" /> Telefone</span>
                  <span className="text-sm text-gray-700 mt-0.5 ml-4">{inst?.contact?.phone || '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1"><UserCheck className="w-3 h-3" /> Responsável</span>
                  <span className="text-sm text-gray-700 mt-0.5 ml-4 break-all">{inst?.contact?.email || '—'}</span>
                </div>
              </div>

              {/* Links compactos */}
              <div className="pt-2 border-t border-gray-200/60 mt-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 mt-2">Links</h4>
                <div className="flex flex-col gap-2">
                  {inst?.link && (
                    <a href={inst.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                      <Globe className="w-3 h-3" /> Site Oficial
                    </a>
                  )}
                  {links.filter(l => !!l.url && !(/site|oficial/i.test(l.label || ''))).map((l) => (
                    <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 hover:underline">
                      <LinkIcon className="w-3 h-3" /> {l.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Linha do Tempo
                </h3>
              </div>
              {loadingHistory ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <AmendmentTimeline
                  history={history}
                  createdAt={amendment.date}
                  detailingSaved={amendment.detailingSaved}
                  hasDetail={hasDetail}
                  currentStatus={amendment.status}
                  dispatchNote={amendment.dispatchNote}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
